// src/components/resources/tools/generators/SocialMediaMockup/socialMediaConfigPersistence.ts

import { clearPersistedGeneratorConfig } from '../shared/usePersistedGeneratorConfig'
import type { EngagementMetrics, InteractionState, SocialMediaConfig, SocialPlatform } from './types'

const SOCIAL_PLATFORMS: readonly SocialPlatform[] = [
  'instagram',
  'x',
  'linkedin',
  'facebook',
  'youtube',
  'tiktok',
  'instagram-reels',
]

function isSocialPlatform(value: unknown): value is SocialPlatform {
  return typeof value === 'string' && SOCIAL_PLATFORMS.includes(value as SocialPlatform)
}

function isInteractionState(value: unknown): value is InteractionState {
  if (!value || typeof value !== 'object') return false
  const record = value as Record<string, unknown>
  return (
    typeof record.liked === 'boolean' &&
    typeof record.bookmarked === 'boolean' &&
    typeof record.commented === 'boolean'
  )
}

function isEngagementMetrics(value: unknown): value is EngagementMetrics {
  if (!value || typeof value !== 'object') return false
  const record = value as Record<string, unknown>
  return (
    typeof record.likes === 'number' &&
    typeof record.comments === 'number' &&
    typeof record.shares === 'number'
  )
}

/**
 * Validates a stored social mockup draft. Returns null and clears corrupt keys.
 *
 * @param {unknown} raw - Parsed JSON from localStorage.
 * @param {string} [storageKey] - Key suffix for clearing invalid drafts.
 * @returns {Partial<SocialMediaConfig> | null} Safe partial config or null.
 */
export function parseStoredSocialConfig(
  raw: unknown,
  storageKey?: string,
): Partial<SocialMediaConfig> | null {
  if (!raw || typeof raw !== 'object') return null
  const record = raw as Record<string, unknown>

  if (record.platform !== undefined && !isSocialPlatform(record.platform)) {
    if (storageKey) clearPersistedGeneratorConfig(storageKey)
    return null
  }
  if (record.metrics !== undefined && !isEngagementMetrics(record.metrics)) {
    if (storageKey) clearPersistedGeneratorConfig(storageKey)
    return null
  }
  if (record.interactions !== undefined && !isInteractionState(record.interactions)) {
    if (storageKey) clearPersistedGeneratorConfig(storageKey)
    return null
  }
  if (record.theme !== undefined && record.theme !== 'light' && record.theme !== 'dark') {
    if (storageKey) clearPersistedGeneratorConfig(storageKey)
    return null
  }

  return {
    ...(isSocialPlatform(record.platform) ? { platform: record.platform } : {}),
    ...(typeof record.username === 'string' ? { username: record.username } : {}),
    ...(typeof record.handle === 'string' ? { handle: record.handle } : {}),
    ...(typeof record.subtitle === 'string' ? { subtitle: record.subtitle } : {}),
    ...(typeof record.headline === 'string' ? { headline: record.headline } : {}),
    ...(typeof record.content === 'string' ? { content: record.content } : {}),
    ...(typeof record.hashtags === 'string' ? { hashtags: record.hashtags } : {}),
    ...(typeof record.background === 'string' ? { background: record.background } : {}),
    ...(typeof record.showMetrics === 'boolean' ? { showMetrics: record.showMetrics } : {}),
    ...(isEngagementMetrics(record.metrics) ? { metrics: record.metrics } : {}),
    ...(typeof record.timestamp === 'string' ? { timestamp: record.timestamp } : {}),
    ...(typeof record.verified === 'boolean' ? { verified: record.verified } : {}),
    ...(record.theme === 'light' || record.theme === 'dark' ? { theme: record.theme } : {}),
    ...(isInteractionState(record.interactions) ? { interactions: record.interactions } : {}),
    ...(typeof record.soundName === 'string' ? { soundName: record.soundName } : {}),
    ...(typeof record.duration === 'string' ? { duration: record.duration } : {}),
  }
}
